import React, { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRightLeft,
  CalendarPlus,
  Check,
  ChevronDown,
  CircleCheck,
  CircleX,
  Edit2,
  Gavel,
  MailPlus,
  RefreshCw,
  Send,
  X,
} from "lucide-react";
import { IconReceiptDollar } from "@tabler/icons-react";
import { AppDialogFrame } from "../../../components/overlays/AppDialogFrame";
import { DetailSectionHeader } from "../../../components/navigation/DetailSectionHeader";
import type { ModuleSectionNavItem } from "../../../components/navigation/ModuleSectionNav";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import { ScheduleModal } from "../components/ScheduleModal";
import { useAdmissionsApplication } from "../adapters/liveData";
import {
  useDomainRouterQuery,
} from "../../../state/domainRouterHooks";
import { isFinancialAidAccessEnabledForApplication } from "../../../shared/financialAidAccess";
import {
  useGetSettingsModuleConfigQuery,
  useListAdmissionsApplicationsQuery,
  useListFacultyMembersQuery,
  useScheduleAdmissionsInterviewMutation,
  useScheduleAdmissionsVisitMutation,
  useSendAdmissionsApplicationMessageMutation,
  useUpdateAdmissionsApplicationMutation,
  useUpdateAdmissionsApplicationStatusMutation,
} from "../../../state/domainRouterApi";

const STATUS_OPTIONS: Array<{
  label: string;
  status: string;
  opensDecisionModal?: boolean;
}> = [
  { label: "Applied", status: "submitted" },
  { label: "In-Progress", status: "under_review" },
  { label: "Scheduled", status: "interview_scheduled" },
  { label: "Decision", status: "decision_pending", opensDecisionModal: true },
  { label: "Waitlisted", status: "waitlisted" },
  { label: "Enrolled", status: "enrolled" },
  { label: "Archived", status: "withdrawn" },
];

const STATUS_LABEL_BY_DOMAIN: Record<string, string> = {
  draft: "Draft",
  submitted: "Applied",
  under_review: "In-Progress",
  interview_scheduled: "Scheduled",
  decision_pending: "Decision",
  accepted: "Accepted",
  accepted_pending_enrollment: "Accepted",
  acceptance_expired: "Archived",
  waitlisted: "Waitlisted",
  denied: "Denied",
  enrolled: "Enrolled",
  withdrawn: "Archived",
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asRecordArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value
        .filter(
          (entry) =>
            entry && typeof entry === "object" && !Array.isArray(entry),
        )
        .map((entry) => entry as Record<string, unknown>)
    : [];

const parsePayloadRecord = (value: unknown): Record<string, unknown> => {
  if (typeof value === "string") {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return {};
    }
  }
  return asRecord(value);
};

const readFamilySelfSchedulingEnabled = (settingsPayload: unknown): boolean => {
  const admissionConfig = asRecord(asRecord(settingsPayload).admissionConfig);
  const interviewScheduling = asRecord(admissionConfig.interviewScheduling);
  return interviewScheduling.familySelfSchedulingEnabled === true;
};

const formatFullName = (firstName: unknown, lastName: unknown): string => {
  const first = String(firstName ?? "").trim();
  const last = String(lastName ?? "").trim();
  return [first, last].filter(Boolean).join(" ").trim();
};

const safeString = (value: unknown): string => String(value ?? "").trim();
const resolveStaffDisplayName = (record: Record<string, unknown>): string => {
  const displayName = safeString(record.displayName) || safeString(record.name);
  if (displayName) return displayName;
  return formatFullName(record.firstName, record.lastName);
};
const UUID_LIKE_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const resolveAssigneeLabel = (...candidates: unknown[]): string => {
  for (const candidate of candidates) {
    const normalized = safeString(candidate);
    if (!normalized || UUID_LIKE_PATTERN.test(normalized)) {
      continue;
    }
    return normalized;
  }
  return "";
};

const parseScheduleUnixSeconds = (
  dateLabel: string | undefined,
  timeLabel: string | undefined,
): number | null => {
  const rawDate = safeString(dateLabel);
  const rawTime = safeString(timeLabel);
  if (!rawDate) return null;

  const date =
    rawDate.toLowerCase() === "today"
      ? new Date()
      : new Date(`${rawDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;

  const timeToken = rawTime.split(/\s+/)[0] ?? "";
  const [hh, mm] = timeToken.split(":").map((part) => Number(part));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;

  date.setHours(hh, mm, 0, 0);
  return Math.floor(date.getTime() / 1000);
};

const formatScheduleDateLabel = (dateLabel: string | undefined): string => {
  const rawDate = safeString(dateLabel);
  if (!rawDate) return "the selected day";
  if (rawDate.toLowerCase() === "today") return "today";
  const parsed = new Date(`${rawDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return rawDate;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatScheduleTimeLabel = (timeLabel: string | undefined): string => {
  const raw = safeString(timeLabel);
  if (!raw) return "";
  const token = raw.split(/\s+/)[0] ?? "";
  const [hh, mm] = token.split(":").map((part) => Number(part));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return raw;
  const normalized = new Date();
  normalized.setHours(hh, mm, 0, 0);
  return normalized.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const unwrapMutationResult = async (mutationResult: unknown): Promise<unknown> => {
  if (
    mutationResult &&
    typeof mutationResult === "object" &&
    typeof (mutationResult as { unwrap?: unknown }).unwrap === "function"
  ) {
    return (mutationResult as { unwrap: () => Promise<unknown> }).unwrap();
  }
  const result = await mutationResult;
  const resultRecord = asRecord(result);
  if ("error" in resultRecord) {
    throw resultRecord.error;
  }
  if ("data" in resultRecord) {
    return resultRecord.data;
  }
  return result;
};

const extractMutationErrorMessage = (error: unknown): string => {
  if (typeof error === "string") return error.trim();
  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>;
    const direct = safeString(record.message);
    if (direct) return direct;
    const nested =
      record.data && typeof record.data === "object"
        ? safeString((record.data as Record<string, unknown>).message)
        : "";
    if (nested) return nested;
  }
  return "";
};

type ApplicantDetailRouteState = {
  openSchedule?: boolean;
  openContact?: boolean;
  composeOffer?: boolean;
  offerSubject?: string;
  offerMessage?: string;
};

const ADMISSIONS_DETAIL_COMPACT_WIDTH_PX = 768;
const ADMISSIONS_OPEN_SCHEDULE_KEY = "ystnj.admissions.openScheduleFor";

export const ApplicantDetail: React.FC = () => {
  const { id } = useParams();
  const routeApplicationId = safeString(id);
  const requiresLegacyResolution = /^\d+$/.test(routeApplicationId);
  const legacyApplicationList = useListAdmissionsApplicationsQuery(
    { limit: 200 },
    { skip: !requiresLegacyResolution },
  );
  const resolvedLegacyId = useMemo(() => {
    if (!requiresLegacyResolution) return "";
    const routeKey = routeApplicationId.toLowerCase();
    const items = legacyApplicationList.data?.items ?? [];
    const matched = items.find((item) => {
      const record = asRecord(item);
      const itemId = safeString(record.id).toLowerCase();
      const code = safeString(record.code).toLowerCase();
      const legacyId =
        safeString(record.legacyId).toLowerCase() ||
        safeString(record.applicationId).toLowerCase() ||
        safeString(record.familyId).toLowerCase();
      return itemId === routeKey || code === routeKey || legacyId === routeKey;
    });
    return safeString(matched?.id ?? items[0]?.id);
  }, [
    legacyApplicationList.data?.items,
    requiresLegacyResolution,
    routeApplicationId,
  ]);
  const applicationId =
    requiresLegacyResolution && resolvedLegacyId
      ? resolvedLegacyId
      : routeApplicationId;
  const location = useLocation();
  const navigate = useNavigate();

  const applicationQuery = useAdmissionsApplication(applicationId);
  const application = applicationQuery.data as unknown as
    | Record<string, unknown>
    | undefined;
  const detailLoaded =
    Boolean(applicationId) &&
    !applicationQuery.isLoading &&
    !applicationQuery.error &&
    Boolean(application);
  const applicant = asRecord(application?.applicant);
  const primaryContact = asRecord(applicant.primaryContact);
  const familyName =
    safeString(applicant.familyName) ||
    safeString(application?.code) ||
    routeApplicationId;
  const primaryContactName = formatFullName(
    primaryContact.firstName,
    primaryContact.lastName,
  );
  const primaryContactEmail = safeString(primaryContact.email);

  const currentDomainStatus = safeString(application?.status).toLowerCase();
  const currentStatusLabel =
    STATUS_LABEL_BY_DOMAIN[currentDomainStatus] ??
    (currentDomainStatus ? currentDomainStatus : "Status");
  const facultyQuery = useListFacultyMembersQuery();
  const settingsQuery = useGetSettingsModuleConfigQuery({
    moduleKey: "staff_settings",
  });
  const settingsPayload = useMemo(
    () => parsePayloadRecord(asRecord(settingsQuery.data).payload),
    [settingsQuery.data],
  );
  const familySelfSchedulingEnabled = useMemo(
    () => readFamilySelfSchedulingEnabled(settingsPayload),
    [settingsPayload],
  );
  const tuitionResolutionQuery = useDomainRouterQuery(
    "resolveFamilyForAdmissionsApplication",
    { applicationId },
    { skip: !applicationId },
  );
  const tuitionConfigurationQuery = useDomainRouterQuery(
    "getAdmissionsTuitionConfiguration",
    { applicationId },
    { skip: !applicationId },
  );
  const tuitionAcceptedChildrenCount =
    tuitionResolutionQuery.data?.acceptedChildren?.length ?? 0;
  const tuitionConfiguration = asRecord(
    tuitionConfigurationQuery.data?.configuration,
  );
  const tuitionTabBadge =
    tuitionAcceptedChildrenCount > 0 &&
    (!Boolean(tuitionConfiguration.configurationComplete) ||
      !safeString(asRecord(tuitionConfiguration.paymentPlan).paymentPlanId))
      ? "!"
      : undefined;

  const facultyStaffMembers = useMemo(
    () =>
      (facultyQuery.data?.items ?? [])
        .map((item) => {
          const record = asRecord(item);
          return {
            id: safeString(record.id),
            name: resolveStaffDisplayName(record),
          };
        })
        .filter((member) => member.name.length > 0),
    [facultyQuery.data?.items],
  );

  const settingsStaffMembers = useMemo(() => {
    return asRecordArray(settingsPayload.staff)
      .map((record) => ({
        id: safeString(record.userId) || safeString(record.id),
        name: resolveStaffDisplayName(record),
      }))
      .filter((member) => member.name.length > 0);
  }, [settingsPayload]);

  const staffMembers = useMemo(() => {
    const seen = new Set<string>();
    const merged = [...facultyStaffMembers, ...settingsStaffMembers];
    const out: Array<{ id: string; name: string }> = [];
    for (const member of merged) {
      const normalizedName = safeString(member.name);
      if (!normalizedName) continue;
      const dedupeKey = normalizedName.toLowerCase();
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push({
        id: safeString(member.id),
        name: normalizedName,
      });
    }
    return out;
  }, [facultyStaffMembers, settingsStaffMembers]);
  const statusOptions = useMemo(
    () =>
      STATUS_OPTIONS.filter(
        (option) =>
          familySelfSchedulingEnabled ||
          option.status !== "interview_scheduled",
      ),
    [familySelfSchedulingEnabled],
  );

  const [updateStatus, updateStatusState] =
    useUpdateAdmissionsApplicationStatusMutation();
  const [scheduleInterview, scheduleInterviewState] =
    useScheduleAdmissionsInterviewMutation();
  const [scheduleVisit, scheduleVisitState] =
    useScheduleAdmissionsVisitMutation();
  const [sendMessage, sendMessageState] =
    useSendAdmissionsApplicationMessageMutation();
  const [updateApplicationMutation, updateApplicationState] =
    useUpdateAdmissionsApplicationMutation();

  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editResetToken, setEditResetToken] = useState(0);
  const [activeToast, setActiveToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [contactForm, setContactForm] = useState({ subject: "", message: "" });
  const [detailSurfaceWidth, setDetailSurfaceWidth] = useState(0);
  const [financialAidAccessOverride, setFinancialAidAccessOverride] =
    useState<boolean | null>(null);

  const detailRootRef = useRef<HTMLElement | null>(null);
  const statusMenuRef = useRef<HTMLDivElement>(null);
  const isChildrenTab = location.pathname.toLowerCase().includes("/children/");
  const isFamilyTab = location.pathname.toLowerCase().includes("/family/");
  const isTuitionTab = location.pathname.toLowerCase().includes("/tuition");
  const isActionButtonsVisible = isChildrenTab || isFamilyTab;
  const isFinancialAidAccessEnabledFromApplication =
    isFinancialAidAccessEnabledForApplication(application);
  const isFinancialAidAccessEnabled =
    financialAidAccessOverride ?? isFinancialAidAccessEnabledFromApplication;
  const isInviteFinancialAidDisabled =
    !detailLoaded ||
    isFinancialAidAccessEnabled ||
    updateApplicationState.isLoading;
  const editLabel = isChildrenTab ? "Edit Children" : "Edit Family";
  const isCompactLayout =
    detailSurfaceWidth > 0 &&
    detailSurfaceWidth <= ADMISSIONS_DETAIL_COMPACT_WIDTH_PX;
  const shouldCollapseTabs =
    detailSurfaceWidth > 0 && detailSurfaceWidth <= 1024;
  const layoutMode = isCompactLayout ? "compact" : "full";
  const rootWidthClass = isCompactLayout
    ? "w-full max-w-[760px] mx-auto"
    : "w-full";
  const showBackText = !isCompactLayout;
  const showPrimaryActionText =
    !shouldCollapseTabs &&
    (detailSurfaceWidth === 0 || detailSurfaceWidth > 980);
  const showEditActionText =
    !shouldCollapseTabs &&
    (detailSurfaceWidth === 0 || detailSurfaceWidth > 1120);
  const sectionNavItems = useMemo<ModuleSectionNavItem[]>(
    () => [
      {
        id: "children",
        label: "Children",
        to: `/school/admissions/children/${applicationId}`,
      },
      {
        id: "family",
        label: "Family",
        to: `/school/admissions/family/${applicationId}`,
      },
      {
        id: "documents",
        label: "Documents",
        to: `/school/admissions/documents/${applicationId}`,
      },
      {
        id: "notes",
        label: "Notes",
        to: `/school/admissions/notes/${applicationId}`,
      },
      {
        id: "tuition",
        label: "Tuition",
        to: `/school/admissions/tuition/${applicationId}`,
        badge: tuitionTabBadge,
      },
    ],
    [applicationId, tuitionTabBadge],
  );
  const headerLeadingClass = shouldCollapseTabs
    ? "flex max-sm:w-full items-center gap-2 min-w-0"
    : isCompactLayout
      ? "flex items-center gap-2 flex-1 min-w-0"
      : "flex items-center gap-4 md:gap-8 flex-1 min-w-0";
  const mainPaddingClass = isCompactLayout ? "px-4" : "px-4 md:px-8";
  const actionRowClass = shouldCollapseTabs
    ? "mb-0 max-sm:w-full justify-end gap-1.5"
    : "mb-[0.3rem] gap-2";
  const scheduleRequestedFromQuery = useMemo(
    () => /(?:^|[?&])openSchedule=1(?:&|$)/i.test(location.search),
    [location.search],
  );
  const scheduleRequestedFromStorage = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      const pendingId = safeString(
        window.sessionStorage.getItem(ADMISSIONS_OPEN_SCHEDULE_KEY),
      );
      if (!pendingId) return false;
      const normalizedPending = pendingId.toLowerCase();
      return (
        normalizedPending === routeApplicationId.toLowerCase() ||
        normalizedPending === applicationId.toLowerCase()
      );
    } catch {
      return false;
    }
  }, [applicationId, routeApplicationId]);

  useEffect(() => {
    const state = (
      location.state && typeof location.state === "object"
        ? (location.state as ApplicantDetailRouteState)
        : {}
    ) as ApplicantDetailRouteState;
    const scheduleRequested =
      Boolean(state.openSchedule) ||
      scheduleRequestedFromQuery ||
      scheduleRequestedFromStorage;
    const shouldOpenSchedule =
      familySelfSchedulingEnabled && scheduleRequested;
    const shouldOpenContact = Boolean(state.openContact);
    const shouldComposeOffer = Boolean(state.composeOffer);

    if (shouldOpenSchedule) {
      setIsScheduleModalOpen(true);
    }

    if (scheduleRequested) {
      if (typeof window !== "undefined") {
        try {
          const pendingId = safeString(
            window.sessionStorage.getItem(ADMISSIONS_OPEN_SCHEDULE_KEY),
          );
          if (pendingId) {
            window.sessionStorage.removeItem(ADMISSIONS_OPEN_SCHEDULE_KEY);
          }
        } catch {
          // Ignore storage cleanup failures.
        }
      }
    }

    if (shouldOpenContact) {
      setIsContactModalOpen(true);
      if (shouldComposeOffer) {
        const defaultSubject = "Admissions Offer";
        const defaultMessage = [
          "Shalom,",
          "",
          "We are ready to issue your admissions offer and share your next steps.",
          "Please review the portal updates and reply with any questions.",
        ].join("\n");
        setContactForm({
          subject: state.offerSubject || defaultSubject,
          message: state.offerMessage || defaultMessage,
        });
      }
    }

    const shouldResetRouteState =
      shouldOpenContact ||
      shouldComposeOffer ||
      (!familySelfSchedulingEnabled && scheduleRequested);
    if (shouldResetRouteState) {
      navigate(
        !familySelfSchedulingEnabled && scheduleRequestedFromQuery
          ? location.pathname
          : `${location.pathname}${location.search}`,
        {
          replace: true,
          state: {},
        },
      );
    }
  }, [
    applicationId,
    familySelfSchedulingEnabled,
    location.pathname,
    location.search,
    location.state,
    navigate,
    routeApplicationId,
    scheduleRequestedFromQuery,
    scheduleRequestedFromStorage,
  ]);

  useEffect(() => {
    if (!familySelfSchedulingEnabled) {
      setIsScheduleModalOpen(false);
    }
  }, [familySelfSchedulingEnabled]);

  useEffect(() => {
    if (!isActionButtonsVisible && isEditing) {
      setIsEditing(false);
    }
  }, [isActionButtonsVisible, isEditing]);

  useEffect(() => {
    setFinancialAidAccessOverride(null);
  }, [applicationId]);

  useEffect(() => {
    if (
      financialAidAccessOverride !== null &&
      financialAidAccessOverride === isFinancialAidAccessEnabledFromApplication
    ) {
      setFinancialAidAccessOverride(null);
    }
  }, [financialAidAccessOverride, isFinancialAidAccessEnabledFromApplication]);

  useEffect(() => {
    const root = detailRootRef.current;
    if (!root) return () => {};

    const syncWidth = () => {
      const parentWidth = root.parentElement?.getBoundingClientRect().width ?? 0;
      const rootWidth = root.getBoundingClientRect().width;
      setDetailSurfaceWidth(Math.round(parentWidth || rootWidth));
    };

    syncWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", syncWidth);
      return () => window.removeEventListener("resize", syncWidth);
    }

    const observer = new ResizeObserver(syncWidth);
    observer.observe(root);
    if (root.parentElement) {
      observer.observe(root.parentElement);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        statusMenuRef.current &&
        !statusMenuRef.current.contains(event.target as Node)
      ) {
        setIsStatusMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onSelectStatus = async (next: {
    label: string;
    status: string;
    opensDecisionModal?: boolean;
  }) => {
    setIsStatusMenuOpen(false);
    if (next.opensDecisionModal) {
      setIsDecisionModalOpen(true);
      return;
    }
    if (!applicationId) return;
    try {
      await updateStatus({ applicationId, status: next.status }).unwrap();
      setActiveToast({ message: "Status updated.", type: "success" });
    } catch {
      setActiveToast({
        message: "Unable to update status right now.",
        type: "error",
      });
    }
  };

  const onInviteToFinancialAid = async () => {
    if (!applicationId || isFinancialAidAccessEnabled) return;
    try {
      await unwrapMutationResult(
        updateApplicationMutation({
          applicationId,
          updates: { financialAidEnabled: true },
        }),
      );
      setFinancialAidAccessOverride(true);
      await Promise.resolve(applicationQuery.refetch()).catch((error) => {
        console.warn(
          "[admissions-financial-aid] Unable to refresh application after enabling access.",
          error,
        );
      });
      setActiveToast({
        message: "Financial Aid access enabled for this family.",
        type: "success",
      });
    } catch (error) {
      setActiveToast({
        message:
          extractMutationErrorMessage(error) ||
          "Unable to invite this family to Financial Aid right now.",
        type: "error",
      });
    }
  };

  const onDecision = async (decision: "accept" | "deny") => {
    if (!applicationId) return;
    const status = decision === "accept" ? "accepted" : "denied";
    try {
      await updateStatus({ applicationId, status }).unwrap();
      setActiveToast({
        message:
          decision === "accept"
            ? "Application accepted."
            : "Application denied.",
        type: "success",
      });
    } catch {
      setActiveToast({
        message: "Unable to save decision right now.",
        type: "error",
      });
    } finally {
      setIsDecisionModalOpen(false);
    }
  };

  const onSendMessage = async () => {
    if (!applicationId) return;
    const subject = contactForm.subject.trim();
    const message = contactForm.message.trim();
    if (!subject || !message) return;
    try {
      const result = await sendMessage({
        applicationId,
        subject,
        message,
      }).unwrap();
      const eventRecord = asRecord(result);
      const metadata = asRecord(eventRecord.metadata);
      const recipientEmail =
        safeString(metadata.recipientEmail) || primaryContactEmail;
      const deliveryState = safeString(metadata.deliveryState);
      const deliveryReason = safeString(metadata.deliveryReason);
      const recipientLabel =
        recipientEmail || primaryContactName || `${familyName} family`;
      if (deliveryState === "not_sent") {
        setActiveToast({
          message: `Message logged for ${recipientLabel}, but email delivery is unavailable${deliveryReason ? ` (${deliveryReason})` : ""}.`,
          type: "error",
        });
      } else {
        setActiveToast({
          message: `Message sent to ${recipientLabel}.`,
          type: "success",
        });
      }
      setContactForm({ subject: "", message: "" });
      setIsContactModalOpen(false);
    } catch (error) {
      const errorMessage = extractMutationErrorMessage(error);
      const hasPrimaryContactEmail = primaryContactEmail.length > 0;
      const normalizedMessage = !hasPrimaryContactEmail
        ? "Primary contact email not available."
        : /SES_FROM_EMAIL/i.test(errorMessage)
          ? "SES_FROM_EMAIL is not configured."
          : "Unable to send message right now. No email was sent.";
      setActiveToast({
        message: normalizedMessage,
        type: "error",
      });
    }
  };

  const onSchedule = async (event: {
    eventType?: string;
    date?: string;
    time?: string;
  }) => {
    if (!applicationId || !familySelfSchedulingEnabled) return false;
    const kind = safeString(event.eventType).toLowerCase();
    const eventLabel = kind.includes("visit") ? "Visit" : "Interview";
    const scheduledDate = formatScheduleDateLabel(event.date);
    const scheduledTime = formatScheduleTimeLabel(event.time);
    const scheduledAt = parseScheduleUnixSeconds(event.date, event.time);
    if (!scheduledAt) {
      setActiveToast({
        message: "Please select a valid date/time.",
        type: "error",
      });
      return false;
    }
    try {
      if (kind.includes("visit")) {
        await scheduleVisit({ applicationId, visitDate: scheduledAt }).unwrap();
      } else {
        await scheduleInterview({
          applicationId,
          interviewDate: scheduledAt,
        }).unwrap();
      }
      setActiveToast({
        message: `${eventLabel} scheduled for ${scheduledDate}${scheduledTime ? ` at ${scheduledTime}` : ""}.`,
        type: "success",
      });
      setIsScheduleModalOpen(false);
      return true;
    } catch (error) {
      const rawError = extractMutationErrorMessage(error);
      setActiveToast({
        message:
          rawError.length > 0 &&
          /unable to schedule right now|no event was saved/i.test(rawError)
            ? rawError
            : "Unable to schedule right now. No event was saved.",
        type: "error",
      });
      return false;
    }
  };

  const pending =
    updateStatusState.isLoading ||
    scheduleInterviewState.isLoading ||
    scheduleVisitState.isLoading ||
    sendMessageState.isLoading;

  return (
    <section
      ref={detailRootRef}
      data-testid="admissions-applicant-detail-root"
      data-loaded={detailLoaded ? "true" : "false"}
      data-layout-mode={layoutMode}
      data-extracted-from="owned:ystnj/internal-owned/school-admissions-v2-1/applicant-detail"
      className={`flex-1 flex flex-col ${rootWidthClass} h-full min-h-0 overflow-visible relative bg-background-light`}
    >
      {familySelfSchedulingEnabled ? (
        <ScheduleModal
          isOpen={isScheduleModalOpen || scheduleRequestedFromQuery}
          onClose={() => {
            setIsScheduleModalOpen(false);
            if (scheduleRequestedFromQuery) {
              navigate(location.pathname, { replace: true, state: {} });
            }
          }}
          onSave={onSchedule}
          initialData={null}
        />
      ) : null}

      {activeToast ? (
        <div
          data-testid="admissions-action-toast"
          className="fixed top-6 left-1/2 -translate-x-1/2 z-[300] w-full max-w-md px-6"
        >
          <div
            className={`px-5 py-4 rounded-2xl shadow-2xl flex items-center gap-4 animate-in slide-in-from-top-6 duration-300 ${
              activeToast.type === "success"
                ? "bg-jungle text-white"
                : "bg-strawberry text-white"
            }`}
          >
            {activeToast.type === "success" ? (
              <CircleCheck size={22} />
            ) : (
              <CircleX size={22} />
            )}
            <p
              data-testid="admissions-action-toast-message"
              className="text-sm font-bold flex-1"
            >
              {activeToast.message}
            </p>
            <button
              onClick={() => setActiveToast(null)}
              aria-label="Dismiss toast"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      ) : null}

      {/* Decision Modal */}
      {isDecisionModalOpen ? (
        <AppDialogFrame
          open={isDecisionModalOpen}
          onClose={() => setIsDecisionModalOpen(false)}
          title="Review & Decision"
          description="Confirm the final decision for this application."
          testId="admissions-decision-modal"
          maxWidthClassName="max-w-md"
          panelClassName="rounded-3xl"
          bodyClassName="space-y-8 text-center"
          bodyPaddingClassName="p-8"
        >
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Gavel size={32} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => onDecision("deny")}
              className="group flex flex-col items-center gap-3 rounded-2xl border border-gray-100 p-6 transition-all hover:border-strawberry/20 hover:bg-strawberry/5"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-strawberry/10 text-strawberry transition-transform group-hover:scale-110">
                <CircleX size={24} />
              </div>
              <span className="font-bold text-irongrey">Deny</span>
            </button>
            <button
              type="button"
              onClick={() => onDecision("accept")}
              className="group flex flex-col items-center gap-3 rounded-2xl border border-gray-100 p-6 transition-all hover:border-primary/20 hover:bg-primary/5"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform group-hover:scale-110">
                <CircleCheck size={24} />
              </div>
              <span className="font-bold text-irongrey">Accept</span>
            </button>
          </div>
        </AppDialogFrame>
      ) : null}

      {/* Contact Modal */}
      {isContactModalOpen ? (
        <AppDialogFrame
          open={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          title="Contact Family"
          description={
            formatFullName(primaryContact.firstName, primaryContact.lastName) ||
            familyName
          }
          testId="admissions-contact-family-modal"
          maxWidthClassName="max-w-2xl"
          panelClassName="rounded-3xl"
          titleClassName="text-xl"
          bodyClassName="space-y-5"
          bodyPaddingClassName="p-8"
          footerPaddingClassName="px-8 py-5"
          footer={
            <>
              <button
                onClick={() => setIsContactModalOpen(false)}
                className="px-6 py-2 text-sm font-bold text-irongrey/60 transition-colors hover:text-irongrey"
              >
                Cancel
              </button>
              <button
                onClick={onSendMessage}
                disabled={
                  pending ||
                  contactForm.subject.trim().length === 0 ||
                  contactForm.message.trim().length === 0
                }
                className="flex items-center gap-2 rounded-xl bg-primary px-8 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send size={16} />
                Send Message
              </button>
            </>
          }
        >
          <div>
            <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-2 block">
              Subject Line
            </label>
            <input
              type="text"
              value={contactForm.subject}
              onChange={(e) =>
                setContactForm((prev) => ({ ...prev, subject: e.target.value }))
              }
              placeholder="Enter subject..."
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-irongrey focus:ring-primary focus:border-primary placeholder:text-irongrey/20"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-2 block">
              Message
            </label>
            <textarea
              value={contactForm.message}
              onChange={(e) =>
                setContactForm((prev) => ({ ...prev, message: e.target.value }))
              }
              rows={6}
              placeholder="Type your message here..."
              className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-irongrey focus:ring-primary focus:border-primary resize-none placeholder:text-irongrey/20"
            />
          </div>
        </AppDialogFrame>
      ) : null}

      <main
        className={`flex-1 ${mainPaddingClass} pb-0 pt-0 flex flex-col min-h-0 overflow-visible`}
      >
        <section className="flex-1 pb-10">
        <DetailSectionHeader
          back={{ kind: "link", to: "/school/admissions/dashboard" }}
          items={sectionNavItems}
          activeId={(() => {
            const path = location.pathname.toLowerCase();
            if (path.includes("/family/")) return "family";
            if (path.includes("/documents/")) return "documents";
            if (path.includes("/notes/")) return "notes";
            if (path.includes("/tuition/")) return "tuition";
            return "children";
          })()}
          ariaLabel="Admissions detail sections"
          compact={shouldCollapseTabs}
          isCompactLayout={isCompactLayout}
          showBackText={showBackText}
          className={`sticky top-0 md:top-[-1rem] z-20 mb-4 flex flex-shrink-0 justify-between border-b border-gray-200 bg-background-light py-2 ${
            shouldCollapseTabs ? "flex-wrap items-start gap-2" : "items-center"
          }`}
          leadingClassName={headerLeadingClass}
          actionsClassName={actionRowClass}
          navContainerTestId="admissions-detail-section-nav"
          desktopClassName="gap-3 md:gap-4"
          desktopBordered={false}
          mobileTriggerFullWidth={false}
          triggerClassName="min-w-[11.5rem]"
          testIds={{
            root: "admissions-detail-tab-nav",
            itemPrefix: "admissions-detail-tab",
            itemIds: {
              children: "admissions-detail-tab-children",
              family: "admissions-detail-tab-family",
              documents: "admissions-detail-tab-documents",
              notes: "admissions-detail-tab-notes",
              tuition: "admissions-detail-tab-tuition",
            },
            mobileTrigger: "admissions-detail-mobile-tab-select",
            mobileDialog: "admissions-detail-mobile-tab-menu",
            mobileItemPrefix: "admissions-detail-mobile-tab",
            mobileItemIds: {
              children: "admissions-detail-mobile-tab-children",
              family: "admissions-detail-mobile-tab-family",
              documents: "admissions-detail-mobile-tab-documents",
              notes: "admissions-detail-mobile-tab-notes",
              tuition: "admissions-detail-mobile-tab-tuition",
            },
          }}
          actions={
            isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setEditResetToken((token) => token + 1);
                    setIsEditing(false);
                  }}
                  className="flex items-center gap-2 text-irongrey/60 font-medium hover:text-irongrey transition-all bg-gray-100 px-4 py-2 rounded-lg text-[90%]"
                >
                  <span>Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="flex items-center gap-2 text-white font-medium hover:bg-primary-hover transition-all bg-primary px-6 py-2 rounded-lg text-[90%] shadow-lg shadow-primary/20"
                >
                  <Check size={18} />
                  <span>Save</span>
                </button>
              </>
            ) : (
              <>
                {familySelfSchedulingEnabled ? (
                  <button
                    type="button"
                    onClick={() => setIsScheduleModalOpen(true)}
                    className={`flex items-center gap-2 text-primary font-medium hover:text-primary-hover transition-all bg-primary/5 py-2 rounded-lg text-[90%] cursor-pointer shrink-0 ${
                      shouldCollapseTabs ? "px-2.5" : "px-2 md:px-4"
                    }`}
                    title="Schedule"
                  >
                    <CalendarPlus size={18} />
                    <span
                      className={showPrimaryActionText ? "inline" : "sr-only"}
                    >
                      Schedule
                    </span>
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={() => setIsContactModalOpen(true)}
                  data-testid="admissions-contact-family-button"
                  className={`flex items-center gap-2 text-primary font-medium hover:text-primary-hover transition-all bg-primary/5 py-2 rounded-lg text-[90%] cursor-pointer shrink-0 ${
                    shouldCollapseTabs ? "px-2.5" : "px-2 md:px-4"
                  }`}
                  title="Contact Family"
                >
                  <MailPlus size={18} />
                  <span
                    className={showPrimaryActionText ? "inline" : "sr-only"}
                  >
                    Contact Family
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setIsStatusMenuOpen((v) => !v)}
                  className={`flex items-center gap-2 text-primary font-medium hover:text-primary-hover transition-all bg-primary/5 py-2 rounded-lg text-[90%] relative cursor-pointer shrink-0 ${
                    shouldCollapseTabs ? "px-2.5" : "px-2 md:px-4"
                  }`}
                  title={`Status: ${currentStatusLabel}`}
                >
                  <ArrowRightLeft
                    size={18}
                    className={isCompactLayout ? "hidden" : "hidden md:block"}
                  />
                  <span className="max-w-[7.5rem] truncate">
                    {currentStatusLabel}
                  </span>
                  <ChevronDown
                    size={14}
                    className={`transition-transform ${isStatusMenuOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {isTuitionTab ? (
                  <button
                    type="button"
                    onClick={() => {
                      void onInviteToFinancialAid();
                    }}
                    disabled={isInviteFinancialAidDisabled}
                    data-testid="admissions-invite-financial-aid-button"
                    className={`flex items-center gap-2 text-primary font-medium hover:text-primary-hover transition-all bg-primary/5 py-2 rounded-lg text-[90%] shrink-0 ${
                      shouldCollapseTabs ? "px-2.5" : "px-2 md:px-4"
                    } ${
                      isFinancialAidAccessEnabled
                        ? "cursor-not-allowed opacity-60"
                        : updateApplicationState.isLoading
                          ? "cursor-wait opacity-70"
                          : "cursor-pointer"
                    }`}
                    title={
                      isFinancialAidAccessEnabled
                        ? "Financial Aid access is already enabled"
                        : "Invite to Financial Aid"
                    }
                  >
                    {updateApplicationState.isLoading ? (
                      <RefreshCw size={18} className="animate-spin" />
                    ) : (
                      <IconReceiptDollar size={18} stroke={2} />
                    )}
                    <span>
                      {isFinancialAidAccessEnabled
                        ? "Enabled"
                        : "Invite to Financial Aid"}
                    </span>
                  </button>
                ) : null}

                {isActionButtonsVisible ? (
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className={`flex items-center gap-2 text-primary font-medium hover:text-primary-hover transition-all bg-primary/5 py-2 rounded-lg text-[90%] cursor-pointer shrink-0 ${
                      shouldCollapseTabs ? "px-2.5" : "px-2 md:px-4"
                    }`}
                    title={editLabel}
                  >
                    <Edit2 size={18} />
                    <span className={showEditActionText ? "inline" : "sr-only"}>
                      {editLabel}
                    </span>
                  </button>
                ) : null}
              </>
            )
          }
        />

        {/* Status Menu */}
        {isStatusMenuOpen ? (
          <div
            ref={statusMenuRef}
            className="absolute w-56 bg-white rounded-xl shadow-soft border border-gray-100 py-2 z-[110] animate-in fade-in zoom-in-95 duration-150"
            style={{ top: "45px", right: "60px" }}
          >
            <div className="px-4 py-2 border-b border-gray-50">
              <span className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">
                Set Status
              </span>
            </div>
            {statusOptions.map((option) => (
              <button
                key={option.status}
                type="button"
                onClick={() => onSelectStatus(option)}
                className="w-full text-left px-4 py-2 text-xs font-semibold hover:bg-gray-50 transition-colors text-irongrey"
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}

          {applicationQuery.isLoading ? (
            <div className="mt-10 text-sm text-irongrey/50">
              Loading application…
            </div>
          ) : null}

          {applicationQuery.error ? (
            <div className="mt-10 bg-white border border-gray-100 rounded-2xl p-6 shadow-card">
              <SupportErrorMessage>
                Unable to load this application right now.
              </SupportErrorMessage>
              <p className="text-xs text-irongrey/50 mt-1">
                Try refreshing. If it persists, contact Intellio support.
              </p>
            </div>
          ) : null}

          {!applicationQuery.isLoading ? (
            <div className="mt-1">
              <Outlet
                context={{
                  applicationId,
                  isEditing,
                  editResetToken,
                  detailStatus: currentStatusLabel,
                  financialAidAccessEnabled: isFinancialAidAccessEnabled,
                  onFinancialAidAccessChange: setFinancialAidAccessOverride,
                  assigneeName:
                    resolveAssigneeLabel(
                      application?.assigneeName,
                      asRecord(application?.assignee).name,
                      application?.assignedTo,
                    ) || undefined,
                  staffMembers,
                }}
              />
            </div>
          ) : null}
        </section>
      </main>
    </section>
  );
};
